/**
 * useRuleCompiler — hooks for the "Compile coach rules" action (Phase 3,
 * coach-knowledge-codification epic; contract docs/RULE_COMPILER_CONTRACT.md).
 *
 * Mirrors the polling pattern from useCalibrationTakes.ts / useTeachingVideo.ts
 * (useQuery + refetchInterval while a job is in flight, useMutation to kick
 * one off) with one addition: `useRuleCompilerRun` tracks whether the
 * CURRENTLY DISPLAYED status is fresh for the run we just triggered, not
 * just "terminal". Compile-rules is re-runnable ("Recompile"), and the
 * status payload carries no job/run id (contract §4) — so on Recompile,
 * a naive "poll until status is completed/failed" can flash the PREVIOUS
 * run's stale suggestions before the backend has even picked the new job up
 * (the worker polls on an interval, same as evaluation_worker.py per
 * CLAUDE.md). We disambiguate using `started_at`: remember the value seen
 * at click time and keep polling until it changes.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { compileRules, getCompileRulesStatus } from '@/services/templateApi';
import type { CompileRulesStatusResponse } from '@/types/ruleCompiler';

const POLLING_INTERVAL_MS = 2_000;
// Safety net: never poll indefinitely, even if the backend fails before
// stamping a fresh started_at on the new run (avoids a stuck spinner /
// polling leak — see the adversarial review note in the PR description).
const MAX_POLL_MS = 5 * 60 * 1000;

// ---------------------------------------------------------------------------
// Low-level query/mutation
// ---------------------------------------------------------------------------

export function useCompileRulesStatus(
  templateId: string | undefined,
  options?: { poll?: boolean; enabled?: boolean },
) {
  return useQuery({
    queryKey: ['compile-rules-status', templateId],
    queryFn: () => getCompileRulesStatus(templateId!),
    enabled: !!templateId && (options?.enabled ?? true),
    refetchInterval: options?.poll ? POLLING_INTERVAL_MS : false,
    refetchOnWindowFocus: true,
  });
}

export function useCompileRules(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => compileRules(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compile-rules-status', templateId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Orchestrating hook — owns the "is a fresh result in yet?" state machine
// ---------------------------------------------------------------------------

export interface UseRuleCompilerRunResult {
  data: CompileRulesStatusResponse | undefined;
  /** True from the moment Compile/Recompile is clicked until a FRESH terminal result lands. */
  isRunning: boolean;
  isLoading: boolean;
  /** Error from kicking off the job (network/permission failure on the POST) — distinct from a `failed` job status. */
  triggerError: string | null;
  trigger: () => Promise<void>;
}

export function useRuleCompilerRun(
  templateId: string | undefined,
  options?: {
    /**
     * Gate the underlying status fetch (and any polling). Pass false when
     * there is clearly nothing to compile yet (no reason_text, no
     * common_mistakes) to avoid a wasted request on every page view.
     * Default true.
     */
    enabled?: boolean;
  },
): UseRuleCompilerRunResult {
  const enabled = options?.enabled ?? true;
  const [activelyPolling, setActivelyPolling] = useState(false);
  const [triggerError, setTriggerError] = useState<string | null>(null);
  // undefined = "no compile click tracked this session" (covers first load
  // and a background run resumed from a page reload — any terminal status
  // observed is authoritative as-is in that case).
  const pendingSinceRef = useRef<string | null | undefined>(undefined);

  const statusQuery = useCompileRulesStatus(templateId, { poll: activelyPolling, enabled });
  const compileMutation = useCompileRules(templateId ?? '');

  const data = statusQuery.data;

  useEffect(() => {
    if (!data) return;
    if (data.status === 'running') {
      setActivelyPolling(true);
      return;
    }
    if (data.status === 'completed' || data.status === 'failed') {
      if (pendingSinceRef.current === undefined || data.started_at !== pendingSinceRef.current) {
        setActivelyPolling(false);
        pendingSinceRef.current = undefined;
      }
      // else: still the stale pre-click snapshot — keep polling.
    }
  }, [data]);

  // Safety net: stop polling after MAX_POLL_MS regardless of server state,
  // so a backend that never stamps a distinguishable started_at can't wedge
  // the UI in a spinner forever.
  useEffect(() => {
    if (!activelyPolling) return undefined;
    const timeoutId = setTimeout(() => {
      setActivelyPolling(false);
      pendingSinceRef.current = undefined;
      setTriggerError(
        'Rule compilation is taking longer than expected. Please refresh and try again.',
      );
    }, MAX_POLL_MS);
    return () => clearTimeout(timeoutId);
  }, [activelyPolling]);

  const trigger = async () => {
    if (!templateId) return;
    setTriggerError(null);
    pendingSinceRef.current = data?.started_at ?? null;
    setActivelyPolling(true);
    try {
      await compileMutation.mutateAsync();
      await statusQuery.refetch();
    } catch (err) {
      setActivelyPolling(false);
      pendingSinceRef.current = undefined;
      setTriggerError(
        err instanceof Error ? err.message : 'Could not start rule compilation. Please try again.',
      );
    }
  };

  return {
    data,
    isRunning: activelyPolling,
    isLoading: statusQuery.isLoading,
    triggerError,
    trigger,
  };
}
